import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-hot-toast";
import { darkNoTransaction, noTransaction } from "../../assets/images";
import { useUser } from "../../context/user-context";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import { errorMessage } from "../../utils/errorMessage";
import TransactionHistoryRefreshButton from "../../components/TransactionHistoryRefreshButton";
import { useRecentTransactions } from "../../hooks/queries/useRecentTransactions";
import { useTransactions } from "../../hooks/queries/useTransactions";
import {
  DASHBOARD_RECENT_LIMIT,
  DASHBOARD_RECENT_POLL_INTERVAL,
} from "../../hooks/queries/activityKeys";
import type { ActivityFilter } from "../../utils/activity/filters";
import type { ActivityItem } from "../../utils/activity/normalizeTransaction";
import ActivityRow from "../activity/ActivityRow";

/**
 * `/recent` takes only `limit` and an optional `coin` — it has no type/subtype
 * param, so a gift-card-only recent list cannot come from it. The gift card tab
 * therefore runs the same query the Activity page's Gift cards tab runs, first
 * page only. Module-scope so the object identity (and the query key) is stable.
 */
const GIFTCARD_FILTER: ActivityFilter = { type: "giftcard" };

/**
 * Dashboard "Recent Transactions".
 *
 * On the Crypto tab this is the mixed `/api/transactions/me/recent` feed —
 * merged and sorted server-side, no client-side fan-out. On the Gift cards tab
 * it's a server-filtered giftcard list instead.
 */
const RecentTransaction = ({
  setSelectedTransaction,
  giftcardOnly = false,
}: any) => {
  const { theme } = useUser();
  const axiosInstance = useAuthAxios();

  // Both hooks always run (rules of hooks); `enabled` decides which one fetches.
  const recent = useRecentTransactions(DASHBOARD_RECENT_LIMIT, {
    enabled: !giftcardOnly,
    refetchInterval: DASHBOARD_RECENT_POLL_INTERVAL,
  });
  const giftcards = useTransactions(GIFTCARD_FILTER, {
    limit: DASHBOARD_RECENT_LIMIT,
    enabled: giftcardOnly,
    refetchInterval: DASHBOARD_RECENT_POLL_INTERVAL,
  });

  const items: ActivityItem[] = giftcardOnly
    ? giftcards.data?.items ?? []
    : recent.items;
  const isPending = giftcardOnly ? giftcards.isPending : recent.isPending;
  // Spin on a background refetch, not on the initial load (which shows the
  // spinner in the list body instead).
  const isRefreshing = giftcardOnly
    ? giftcards.isFetching && !giftcards.isPending
    : recent.isFetching && !recent.isPending;
  const isError = giftcardOnly ? giftcards.isError : recent.isError;
  const refetch = giftcardOnly ? giftcards.refetch : recent.refetch;

  // Gift card thumbnails, only when a row actually needs one.
  const hasGiftCards = items.some((i: ActivityItem) => i.type === "giftcard");
  const { data: catalogue } = useQuery({
    queryKey: ["get-giftcards"],
    queryFn: async () => (await axiosInstance.get(API.getGiftCards)).data.data,
    enabled: hasGiftCards,
    staleTime: 5 * 60 * 1000,
  });
  const giftCardImages = useMemo(() => {
    const map: Record<string, string> = {};
    (catalogue?.giftCards ?? []).forEach((card: any) => {
      if (card?._id) map[card._id] = card.thumbnail ?? "";
    });
    return map;
  }, [catalogue]);

  useEffect(() => {
    if (isError) toast.error(errorMessage("Could not load recent transactions"));
  }, [isError]);

  const emptyLabel = giftcardOnly
    ? "No gift card transactions yet"
    : "No transactions yet";

  return (
    <div className="w-full font-manrope bg-white dark:bg-[#1C2227] rounded-2xl pb-4 mt-6 h-[400px] overflow-auto">
      <div className="w-full sticky top-0 flex justify-between bg-white dark:bg-[#1C2227] px-4 items-center py-4">
        <h3 className="font-bold text-paragraph text-[13px] dark:text-white">
          Recent Transactions
        </h3>
        <div className="flex items-center gap-1">
          <TransactionHistoryRefreshButton
            onRefresh={() => refetch()}
            isRefreshing={isRefreshing}
          />
          <Link
            to="/activity"
            className="text-[13px] transition ease-out duration-300 text-secondary_button flex justify-center items-center"
          >
            See all
          </Link>
        </div>
      </div>

      {isPending ? (
        <div className="w-full flex justify-center items-center py-16">
          <ClipLoader color="#5E91FF" size={40} />
        </div>
      ) : items.length ? (
        <div className="w-full px-2">
          {items.map((item: ActivityItem) => (
            <ActivityRow
              key={item.id}
              item={item}
              giftCardImage={giftCardImages[item.iconMeta.value]}
              onSelect={setSelectedTransaction}
            />
          ))}
        </div>
      ) : (
        <div className="w-full flex flex-col justify-center items-center py-12">
          <img
            src={theme === "dark" ? darkNoTransaction : noTransaction}
            alt=""
            className="w-[100px] h-[100px]"
          />
          <h4 className="text-[13px] text-gray-500 dark:text-gray-400 mt-3">
            {emptyLabel}
          </h4>
        </div>
      )}
    </div>
  );
};

export default RecentTransaction;
